import { Component, HostListener, Inject, ChangeDetectorRef } from '@angular/core';
import {
  BookingDTO,
  ResourceApi,
  FlightApi,
  OrderApi,
  FeatureApi,
  PrepaidTicketApi,
  ContactInformationApi,
  CarApi,
  EngineTypeDTO,
  FeatureWithPriceDTO,
  FlightDTO,
  MainFeatureDTO,
  OrderDTO,
  PrepaidTicketDTO,
  ResourceDTO,
  ResourceStatusDTO,
  VehicleTypeDTO,
  Resource,
  OrderItemDTOOrderItemTypeEnum,
} from '@lkp/parking-sdk';
import {
  RESOURCE_API,
  FLIGHT_API,
  ORDER_API,
  FEATURE_API,
  PREPAID_TICKET_API,
  CAR_API,
  CONTACT_INFORMATION_API,
} from '../../core/providers/api.module';
import { Subject, takeUntil, map, debounceTime, forkJoin, of } from 'rxjs';
import { DateService } from 'src/app/core/services/date.service';
import {
  AddOrderItem,
  CheckoutOrderPayLater,
  ResetOrderState,
  UpdateOrderItem,
} from 'src/app/core/states/orders/orders.action';
import {
  FormControl,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { PrepaidSearchListener } from 'src/app/core/states/prepaid/prepaid.actions';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  BookingSearchListener,
  DoneEditBooking,
  GetEditBooking,
} from 'src/app/core/states/bookings/booking.actions';
import { BookingsState } from 'src/app/core/states/bookings/bookings.state';
import { UsersState } from 'src/app/core/states/users/users.state';
import { Router } from '@angular/router';
import { OrderService } from 'src/app/core/services/order.service';
import { OrdersState } from 'src/app/core/states/orders/orders.state';
import { BookingService } from 'src/app/core/services/booking.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { normalizeErrorMessage } from 'src/app/core/utils/error-normalize';

dayjs.extend(utc);

@Component({
    selector: 'app-booking-create',
    templateUrl: './booking-create.component.html',
    styleUrls: ['./booking-create.component.scss'],
    standalone: false
})
export class BookingCreateComponent {
  /* Confirm reload window */
  @HostListener('window:beforeunload', ['$event'])
  confirmReload(event: BeforeUnloadEvent) {
    event.preventDefault();
  }
  readonly onDestroy$ = new Subject<void>();
  booking?: BookingDTO;
  editBooking?: BookingDTO;
  editOrder?: OrderDTO;

  invalidFormMessage?: string;
  hasChanges = false; // Used by confirm-guard service
  showNextSpinner: boolean = false;
  showDuplicateSpinner: boolean = false;

  /* Form */
  bookingForm?: FormGroup;
  selectedFeatures: FeatureWithPriceDTO[] = [];

  /* Status */
  parkingResources: ResourceDTO[] = [];
  parkingStatusResources?: ResourceStatusDTO[];

  /* Flight options */
  arrivalFlightOptions: FlightDTO[] = [];
  departureFlightOptions: FlightDTO[] = [];
  filteredArrivalFlightOptions: FlightDTO[] = [];
  filteredDepartureFlightOptions: FlightDTO[] = [];

  /* Other options */
  availableMainFeatures: MainFeatureDTO[] = [];
  availablePrepaidTickets: PrepaidTicketDTO[] = [];

  /* Booking price pre-edit */
  bookingPriceBeforeEdit: number | undefined = 0;
  totalPrice: number = 0;

  /* Vehicle */
  vehicleTypes?: VehicleTypeDTO[];
  engineTypes?: EngineTypeDTO[];
  checkedVehicleType?: VehicleTypeDTO;
  checkedEngineType?: EngineTypeDTO;

  constructor(
    @Inject(RESOURCE_API) readonly resourceApi: ResourceApi,
    @Inject(FLIGHT_API) readonly flightApi: FlightApi,
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    @Inject(FEATURE_API) readonly featureApi: FeatureApi,
    @Inject(PREPAID_TICKET_API) readonly prepaidTicketApi: PrepaidTicketApi,
    @Inject(CONTACT_INFORMATION_API)
    readonly contactInformationApi: ContactInformationApi,
    @Inject(CAR_API) readonly carApi: CarApi,
    readonly dateService: DateService,
    readonly formBuilder: FormBuilder,
    readonly store: Store,
    readonly router: Router,
    readonly orderService: OrderService,
    readonly bookingService: BookingService,
    readonly dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    actions$: Actions
  ) {
    actions$
      .pipe(
        ofActionDispatched(PrepaidSearchListener),
        map((action: PrepaidSearchListener) => action.term),
        debounceTime(300),
        takeUntil(this.onDestroy$)
      )
      .subscribe((term) => {
        /* Check for prepaid tickets */
        this.checkPrepaidTickets(term);

        /* Check for contact information */
        this.checkContactInformation(term);
      });
    actions$
      .pipe(
        ofActionDispatched(BookingSearchListener),
        map((action: BookingSearchListener) => action.term),
        debounceTime(300),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.suggestParking();
        this.updateOrder();
      });
  }

  ngOnInit() {
    /* Get parking resources */
    this.resourceApi
      .getResources()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.parkingResources = v.map((resourceStatus) => ({
          ...resourceStatus,
          resource: undefined,
        }));
      });

    /* When used to edit booking, get booking id from url and if id, get the bookning to edit */
    const editBookingId = Number(this.router.url.split('/')[2]);

    this.store
      .dispatch(new GetEditBooking(editBookingId || undefined))
      .subscribe(() => {
        /* When get edit booking is finished, get current edit booking */
        this.store
          .select(BookingsState.currentEditBooking)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((editBooking) => {
            this.editBooking = editBooking;
            this.booking = { ...this.editBooking };

            /* Vehicle/engine types and (when editing) the booking's order are
               independent of each other - fetch them concurrently instead of
               as a sequential chain to cut load time. */
            forkJoin({
              vehicleTypes: this.carApi.getVehicleTypes(),
              engineTypes: this.carApi.getEngineTypes(),
              orders: this.editBooking?.bookingId
                ? this.orderApi.getOrdersAdmin({
                    size: 1,
                    bookingId: this.editBooking.bookingId,
                  })
                : of(undefined),
            })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(({ vehicleTypes, engineTypes, orders }) => {
                this.vehicleTypes = vehicleTypes;
                this.engineTypes = engineTypes;

                if (orders?.content?.length) {
                  this.editOrder = orders.content[0];
                  this.bookingPriceBeforeEdit = orders.content[0].totalAmount;
                }

                {
                    /* Set checked value for radio buttons */
                    this.checkedEngineType =
                      this.editBooking?.engineType ||
                      this.engineTypes?.[0] ||
                      undefined;

                    this.checkedVehicleType =
                      this.editBooking?.vehicleType ||
                      this.vehicleTypes?.[0] ||
                      undefined;

                    /* Create form group, set edit value or undefined */
                    this.bookingForm = this.formBuilder.group({
                      departureDate: new FormControl<Date | undefined>(
                        this.editBooking?.departureFlight
                          ?.scheduledDepartureDate || undefined
                      ),
                      arrivalDate: new FormControl<Date | undefined>(
                        this.editBooking?.arrivalFlight?.scheduledArrivalDate ||
                          undefined
                      ),
                      selectedDepartureFlight: new FormControl<
                        FlightDTO | string | undefined
                      >(this.editBooking?.departureFlight || undefined),
                      selectedArrivalFlight: new FormControl<
                        FlightDTO | string | undefined
                      >(this.editBooking?.arrivalFlight || undefined),
                      parkingStartDate: new FormControl<
                        Date | string | undefined
                      >(this.editBooking?.departureDate || undefined, [
                        Validators.required,
                      ]),
                      parkingEndDate: new FormControl<
                        Date | string | undefined
                      >(this.editBooking?.arrivalDate || undefined, [
                        Validators.required,
                      ]),
                      parkingStartTime: new FormControl<string | undefined>(
                        this.editBooking?.departureDate
                          ? this.dateService.formatTime(
                              this.editBooking.departureDate
                            )
                          : undefined,
                        [Validators.required]
                      ),
                      parkingEndTime: new FormControl<string | undefined>(
                        this.editBooking?.arrivalDate
                          ? this.dateService.formatTime(
                              this.editBooking.arrivalDate
                            )
                          : undefined,
                        [Validators.required]
                      ),
                      location: new FormControl<ResourceDTO | undefined>(
                        this.editBooking?.resource || undefined,
                        [Validators.required]
                      ),
                      name: new FormControl<string | undefined>(
                        this.editBooking?.name || undefined,
                        [Validators.required]
                      ),
                      phoneNumber: new FormControl<string | undefined>(
                        this.editBooking?.phone || undefined,
                        [Validators.required, Validators.minLength(7)]
                      ),
                      email: new FormControl<string | undefined>(
                        this.editBooking?.email || undefined,
                        [Validators.required, Validators.email]
                      ),
                      registrationNumber: new FormControl<string | undefined>(
                        this.editBooking?.registrationNumber || undefined,
                        [Validators.required]
                      ),
                      passengers: new FormControl<number | undefined>(
                        this.editBooking?.qtyPersons || undefined,
                        [Validators.required]
                      ),
                      notOnlyCarryOnLuggage: new FormControl<boolean>(
                        this.editBooking?.handLuggageOnly == undefined
                          ? true
                          : !this.editBooking.handLuggageOnly
                      ),
                      prepaid: new FormControl<PrepaidTicketDTO | 'none'>(
                        this.editBooking?.prepaidTicket || 'none'
                      ),
                      comment: new FormControl<string | undefined>(
                        this.editBooking?.comment || undefined
                      ),
                      childSafetySeat: new FormControl<boolean | undefined>(
                        this.editBooking?.childSafetySeat
                      ),
                      childBoosterSeat: new FormControl<boolean | undefined>(
                        this.editBooking?.boosterSeat
                      ),
                      newsLetter: new FormControl<boolean | undefined>(
                        this.editBooking?.acceptNewsletter
                      ),
                      vehicleType: new FormControl<VehicleTypeDTO | undefined>(
                        this.editBooking?.vehicleType ||
                          this.vehicleTypes?.[0] ||
                          undefined
                      ),
                      engineType: new FormControl<EngineTypeDTO | undefined>(
                        this.editBooking?.engineType ||
                          this.engineTypes?.[0] ||
                          undefined
                      ),
                    });

                    /* Features (editOrder/bookingPriceBeforeEdit already set above from the concurrent orders fetch) */
                    this.getAndSetFeatures();

                    /* Listen to input changes */
                    this.bookingForm
                      ?.get('departureDate')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe((v) => {
                        this.bookingForm?.controls[
                          'selectedDepartureFlight'
                        ].setValue(undefined);
                        this.suggestParking();
                        this.updateOrder();
                      });
                    this.bookingForm
                      ?.get('arrivalDate')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe((v) => {
                        this.bookingForm?.controls[
                          'selectedArrivalFlight'
                        ].setValue(undefined);
                        this.suggestParking();
                        this.updateOrder();
                      });
                    this.bookingForm
                      ?.get('selectedDepartureFlight')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe((v) => {
                        this.filter(v, 'departure');
                        /* Debounce before suggesting parking and update booking */
                        this.store.dispatch(new BookingSearchListener());
                      });
                    this.bookingForm
                      ?.get('selectedArrivalFlight')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe((v) => {
                        this.filter(v, 'arrival');
                        /* Debounce before suggesting parking and update booking */
                        this.store.dispatch(new BookingSearchListener());
                      });
                    this.bookingForm
                      ?.get('registrationNumber')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe((v) => {
                        this.invalidFormMessage = '';
                        v && this.store.dispatch(new PrepaidSearchListener(v));
                      });
                    this.bookingForm
                      ?.get('parkingStartDate')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                        this.checkAvailability;
                      });
                    this.bookingForm
                      ?.get('parkingStartTime')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                        this.checkAvailability;
                      });
                    this.bookingForm
                      ?.get('parkingEndDate')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                        this.checkAvailability;
                      });
                    this.bookingForm
                      ?.get('parkingEndTime')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                        this.checkAvailability;
                      });
                    this.bookingForm
                      ?.get('passengers')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                      });
                    this.bookingForm
                      ?.get('email')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                      });
                    this.bookingForm
                      ?.get('phoneNumber')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                      });
                    this.bookingForm
                      ?.get('name')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                      });
                    this.bookingForm
                      ?.get('location')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.invalidFormMessage = '';
                        this.updateOrder();
                        this.getAndSetFeatures();
                        if (
                          this.bookingForm?.controls['registrationNumber'].value
                        )
                          this.checkPrepaidTickets(
                            this.bookingForm?.controls['registrationNumber']
                              .value
                          );
                      });
                    this.bookingForm
                      ?.get('vehicleType')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.updateOrder();
                        this.getAndSetFeatures();
                      });
                    this.bookingForm
                      ?.get('engineType')
                      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.updateOrder();
                        this.getAndSetFeatures();
                      });

                    /* Listen to form group changes */
                    this.bookingForm?.valueChanges
                      .pipe(takeUntil(this.onDestroy$))
                      .subscribe(() => {
                        this.hasChanges = this.bookingForm?.dirty || false;
                      });

                    this.updateOrder();

                    /* Add, if editBooking get flight options, prepaidtickets, availability */
                    if (editBooking) {
                      this.checkAvailability();
                      this.updateOrder();
                      editBooking.registrationNumber &&
                        this.checkPrepaidTickets(
                          editBooking.registrationNumber
                        );
                      if (
                        editBooking.departureFlight ||
                        editBooking.departureFlightNumber
                      ) {
                        this.findFlights('departure');
                      }
                      if (
                        editBooking.arrivalFlight ||
                        editBooking.arrivalFlightNumber
                      ) {
                        this.findFlights('arrival');
                      }
                    }
                }
              });
          });
      });
  }

  navBack() {
    this.router.navigate(['hem']);
  }

  cancel() {
    if (this.editBooking?.bookingId) {
      this.router.navigate(['bokning', this.editBooking.bookingId]);
    } else {
      this.router.navigate(['hem']);
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(new DoneEditBooking()).subscribe(() => {
      this.onDestroy$.next(undefined);
      this.onDestroy$.complete();
    });
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  hasFeature(order: OrderDTO | undefined, featureId: number): boolean {
    return this.booking && order
      ? this.bookingService.hasFeature(order, featureId, this.booking)
      : false;
  }

  addFeatureFormControl(name: number, value: boolean): void {
    if (this.bookingForm)
      this.bookingForm.addControl(
        name.toString(),
        new FormControl<FeatureWithPriceDTO | boolean>(value)
      );
  }

  /* Functions to send to angular material autocomplete component, needs to be arrowfn to be able to use local functions in here. */
  public displayDepartureFn = (option: FlightDTO): string => {
    return option
      ? `${option.flightNumber} ${option.arrivalAirport} ${this.formatTime(
          option.scheduledDepartureDate
        )}`
      : '';
  };
  public displayArrivalFn = (option: FlightDTO): string => {
    return option
      ? `${option.flightNumber} ${option.departureAirport} ${this.formatTime(
          option.scheduledArrivalDate
        )}`
      : '';
  };
  comparePrepaidObjects(t1: any, t2: any): boolean {
    return t1 && t2 ? t1.prepaidTicketId === t2.prepaidTicketId : t1 === t2;
  }
  /* End of angular material functions */

  filter(term: string | object, direction: 'departure' | 'arrival'): void {
    /* Term can be object or string */
    if (typeof term == 'string') {
      if (direction === 'departure') {
        this.filteredDepartureFlightOptions = this.bookingService.filterFlights(
          this.departureFlightOptions,
          'departure',
          term
        );
      } else {
        this.filteredArrivalFlightOptions = this.bookingService.filterFlights(
          this.arrivalFlightOptions,
          'arrival',
          term
        );
      }
    }
  }

  updateOrder(): void {
    this.updateBooking();
    this.calculateTotal();
  }

  calculateTotal() {
    const selectedPrepaid = this.bookingForm?.controls['prepaid']?.value;
    const effectivePrepaid =
      selectedPrepaid && selectedPrepaid !== 'none' && this.ticketIsFull(selectedPrepaid)
        ? 'none'
        : selectedPrepaid;
    this.totalPrice = this.bookingService.calculateTotalPrice(
      this.bookingForm?.controls['location']?.value,
      this.parkingStatusResources,
      this.selectedFeatures,
      effectivePrepaid
    );
  }

  setFlightValues() {
    /* Check if selected flight is string/object and set correct variable */
    let departureFlightObj: FlightDTO | undefined = undefined;
    let arrivalFlightObj: FlightDTO | undefined = undefined;
    let arrivalFlightInput: string | undefined = undefined;
    let departureFlightInput: string | undefined = undefined;

    if (this.bookingForm) {
      if (
        typeof this.bookingForm.controls['selectedDepartureFlight']?.value ==
        'object'
      ) {
        departureFlightObj =
          this.bookingForm.controls['selectedDepartureFlight']?.value;
        departureFlightInput = undefined;
      } else {
        departureFlightInput =
          this.bookingForm.controls['selectedDepartureFlight']?.value;
        departureFlightObj = undefined;
      }
      if (
        typeof this.bookingForm.controls['selectedArrivalFlight']?.value ==
        'object'
      ) {
        arrivalFlightObj =
          this.bookingForm.controls['selectedArrivalFlight']?.value;
        arrivalFlightInput = undefined;
      } else {
        arrivalFlightInput =
          this.bookingForm.controls['selectedArrivalFlight']?.value;
        arrivalFlightObj = undefined;
      }
    }

    return {
      departureFlightObj,
      arrivalFlightObj,
      arrivalFlightInput,
      departureFlightInput,
    };
  }

  updateBooking(): void {
    if (this.bookingForm) {
      this.checkAvailability();
      const parkingStart: Date | undefined = this.dateService.composeDatetime(
        this.bookingForm.controls['parkingStartDate'].value || undefined,
        this.bookingForm.controls['parkingStartTime'].value || undefined
      );
      const parkingEnd: Date | undefined = this.dateService.composeDatetime(
        this.bookingForm.controls['parkingEndDate'].value || undefined,
        this.bookingForm.controls['parkingEndTime'].value || undefined
      );

      const prepaidTicket = this.bookingForm.controls['prepaid']?.value;

      let {
        departureFlightObj,
        arrivalFlightObj,
        arrivalFlightInput,
        departureFlightInput,
      } = this.setFlightValues();

      this.booking = {
        ...this.booking,
        departureDate: parkingStart || undefined,
        arrivalDate: parkingEnd || undefined,
        departureFlight: departureFlightObj || undefined,
        arrivalFlight: arrivalFlightObj || undefined,
        departureFlightNumber: departureFlightInput || undefined, // If no flight selected, use input
        arrivalFlightNumber: arrivalFlightInput || undefined, // If no flight selected, use input
        travelHomeFrom:
          this.bookingForm.controls['selectedArrivalFlight']?.value
            ?.departureAirport || undefined,
        registrationNumber:
          this.bookingForm.controls[
            'registrationNumber'
          ]?.value?.toUpperCase() || undefined,
        resource: this.bookingForm.controls['location']?.value || undefined,
        qtyPersons: this.bookingForm.controls['passengers']?.value || undefined,
        name: this.bookingForm.controls['name']?.value || undefined,
        phone: this.bookingForm.controls['phoneNumber']?.value || undefined,
        email: this.bookingForm.controls['email']?.value || undefined,
        handLuggageOnly:
          this.bookingForm.controls['notOnlyCarryOnLuggage']?.value == true
            ? false
            : true,
        comment: this.bookingForm.controls['comment']?.value || undefined,
        prepaidTicket:
          prepaidTicket !== 'none' && !this.ticketIsFull(prepaidTicket)
            ? prepaidTicket
            : undefined,
        childSafetySeat:
          this.bookingForm.controls['childSafetySeat']?.value || undefined,
        boosterSeat:
          this.bookingForm.controls['childBoosterSeat']?.value || undefined,
        acceptNewsletter:
          this.bookingForm.controls['newsLetter']?.value || undefined,
        vehicleType:
          this.bookingForm.controls['vehicleType']?.value || undefined,
        engineType: this.bookingForm.controls['engineType']?.value || undefined,
      };
    }
  }

  updateFeatures(): void {
    /* Filter selected features and connect car to feature */
    this.selectedFeatures = this.filterSelectedFeatures().map((feature) => {
      return {
        ...feature,
        registrationNumber: this.booking?.registrationNumber,
      };
    });
    this.calculateTotal();
  }

  getAndSetFeatures(): void {
    /* Create form controls for features, set previous value or false */
    this.featureApi
      .getMainFeaturesByBooking({ bookingDTO: this.booking! })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.availableMainFeatures = v;
        v.forEach((mainFeature) => {
          mainFeature.features?.forEach((feature) => {
            if (feature.featureId !== undefined && this.bookingForm) {
              this.addFeatureFormControl(
                feature.featureId,
                this.hasFeature(this.editOrder, feature.featureId)
              );
            }
          });
        });
        this.updateFeatures();
        this.updateOrder();
      });
  }

  getVehicleOptions(): void {
    this.carApi
      .getVehicleTypes()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.vehicleTypes = v;
      });

    this.carApi
      .getEngineTypes()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.engineTypes = v;
      });
  }

  formatOnInputChange(
    event: Event,
    type:
      | 'parkingStartTime'
      | 'parkingEndTime'
      | 'departureDate'
      | 'arrivalDate'
  ): void {
    const inputValue = (event.target as HTMLInputElement).value;

    if (this.bookingForm) {
      switch (type) {
        case 'parkingStartTime':
          this.bookingForm.controls['parkingStartTime'].setValue(
            this.formatTimeInput(this.sanitizeNumericInput(inputValue))
          );
          break;
        case 'parkingEndTime':
          this.bookingForm.controls['parkingEndTime'].setValue(
            this.formatTimeInput(this.sanitizeNumericInput(inputValue))
          );
          break;
        case 'departureDate':
          if (inputValue.length >= 8 && this.formatDateInput(inputValue))
            this.bookingForm.controls['departureDate'].setValue(
              this.formatDateInput(inputValue)
            );
          break;
        case 'arrivalDate':
          if (inputValue.length >= 8 && this.formatDateInput(inputValue))
            this.bookingForm.controls['arrivalDate'].setValue(
              this.formatDateInput(inputValue)
            );
          break;
      }
    }
  }

  sanitizeNumericInput(input: string): string {
    // Remove non-numeric characters
    const numericInput = input.replace(/[^0-9]/g, '');

    // Format to HH:mm
    return numericInput;
  }

  formatTimeInput(input: string): string {
    if (input.length >= 4) {
      const hours = input.slice(0, 2);
      const minutes = input.slice(2, 4);
      const formattedTime = `${hours}:${minutes}`;

      // Check for valid hours (0-23)
      if (+hours >= 0 && +hours <= 23) {
        return formattedTime;
      } else {
        return 'fel tidsformat';
      }
    } else {
      return input;
    }
  }

  formatDateInput(input: string): Date | undefined {
    let inputArray: string[] = [];
    if (input.length >= 8) {
      inputArray = input.split('');
      if (inputArray[4] !== '-') inputArray.splice(4, 0, '-');
      if (inputArray[7] !== '-') inputArray.splice(7, 0, '-');
    }
    return inputArray.length === 10 ? new Date(inputArray.join('')) : undefined;
  }

  formatTime(date: Date | string | undefined): string {
    return (date && this.dateService.formatTime(date)) || '';
  }

  formatDate(date: Date | string | undefined): string {
    return (date && this.dateService.formatDate(date)) || '';
  }

  findFlights(direction: 'departure' | 'arrival'): void {
    if (this.bookingForm) {
      let date =
        direction == 'departure'
          ? this.bookingForm.controls['departureDate'].value
          : this.bookingForm.controls['arrivalDate'].value;

      if (date) {
        this.flightApi
          .getFlightsAdmin({
            /* Without utc(true), the date is interpreted 
            as UTC instead of local time and gets the flights 
            from the previous day instead of the day the user 
            picks from datepicker
            */
            flightDate: dayjs(date).utc(true).toDate(),
            flightType: direction.toLocaleUpperCase(),
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((v) => {
            if (this.bookingForm) {
              if (direction == 'departure') {
                /* Set flight options */
                this.departureFlightOptions = v;
                this.filteredDepartureFlightOptions = [];
              } else {
                this.arrivalFlightOptions = v;
                this.filteredArrivalFlightOptions = [];
              }
            }
          });
      } else {
        console.error('Error fetching flights');
      }
    }
  }

  private createDate(date: string, time: string): Date {
    const inputDate = new Date(date);
    const year = inputDate.getFullYear();
    const month = (inputDate.getMonth() + 1).toString().padStart(2, '0');
    const day = inputDate.getDate().toString().padStart(2, '0');

    if (!time || time === '') {
      time = '00:00';
    }

    return new Date(`${year}-${month}-${day}T${time}:00`);
  }

  suggestParking(): void {
    if (this.bookingForm) {
      if (
        (!this.bookingForm.controls['departureDate'].value &&
          !this.bookingForm.controls['selectedDepartureFlight'].value &&
          !this.bookingForm.controls['arrivalDate'].value &&
          !this.bookingForm.controls['selectedArrivalFlight'].value) ||
        (this.bookingForm.controls['departureDate'].value &&
          this.bookingForm.controls['selectedDepartureFlight'].value &&
          this.bookingForm.controls['arrivalDate'].value &&
          this.bookingForm.controls['selectedArrivalFlight'].value)
      ) {
        /* Update entire parking period */

        /* Use selected flight if we have it, otherwise use old time before date (can be undefined) */
        const oldParkingFrom = this.createDate(
          this.bookingForm.controls['departureDate'].value,
          this.bookingForm.controls['parkingStartTime'].value
        );
        const oldParkingTo = this.createDate(
          this.bookingForm.controls['arrivalDate'].value,
          this.bookingForm.controls['parkingEndTime'].value
        );
        const suggestedParkingFrom =
          this.bookingForm.controls['selectedDepartureFlight'].value
            ?.suggestedParkingFrom ||
          oldParkingFrom ||
          this.bookingForm.controls['departureDate'].value;

        const suggestedParkingTo =
          this.bookingForm.controls['selectedArrivalFlight'].value
            ?.suggestedParkingTo ||
          oldParkingTo ||
          this.bookingForm.controls['arrivalDate'].value;

        this.bookingForm.controls['parkingStartTime'].setValue(
          this.formatTime(suggestedParkingFrom)
        );
        this.bookingForm.controls['parkingStartDate'].setValue(
          this.formatDate(suggestedParkingFrom)
        );
        this.bookingForm.controls['parkingEndTime'].setValue(
          this.formatTime(suggestedParkingTo)
        );
        this.bookingForm.controls['parkingEndDate'].setValue(
          this.formatDate(suggestedParkingTo)
        );
      } else if (
        !this.bookingForm.controls['departureDate'].value &&
        this.bookingForm.controls['arrivalDate'].value
      ) {
        /* Only suggest parking to */
        const suggestedParkingTo =
          this.bookingForm.controls['selectedArrivalFlight'].value
            ?.suggestedParkingTo ||
          this.bookingForm.controls['arrivalDate'].value;

        this.bookingForm.controls['parkingEndTime'].setValue(
          this.formatTime(suggestedParkingTo)
        );
        this.bookingForm.controls['parkingEndDate'].setValue(
          this.formatDate(suggestedParkingTo)
        );
      }
    }
    this.checkAvailability();
  }

  checkAvailability() {
    if (
      this.bookingForm &&
      this.bookingForm.controls['parkingStartDate'].value &&
      this.bookingForm.controls['parkingStartTime'].value &&
      this.bookingForm.controls['parkingEndDate'].value &&
      this.bookingForm.controls['parkingEndTime'].value
    ) {
      const fromDate = this.createDate(
        this.bookingForm.controls['parkingStartDate'].value,
        this.bookingForm.controls['parkingStartTime'].value
      );
      const toDate = this.createDate(
        this.bookingForm.controls['parkingEndDate'].value,
        this.bookingForm.controls['parkingEndTime'].value
      );

      this.resourceApi
        .getAvailableResourcesByDates({
          fromDate: fromDate,
          toDate: toDate,
          bookingId: this.booking?.bookingId
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((v) => {
          this.parkingStatusResources = v || [];
          this.calculateTotal();
        });
    } else {
      this.parkingStatusResources = [];
    }
  }

  checkPrepaidTickets(term: string) {
    if (
      this.booking &&
      this.booking.resource?.resourceId &&
      this.booking.departureDate &&
      this.booking.arrivalDate
    ) {
      this.prepaidTicketApi
        .findPrepaidTicketsByBookingAdmin({
          registrationNumber: term,
          resourceId: this.booking.resource.resourceId,
          departureDate: this.booking.departureDate,
          arrivalDate: this.booking.arrivalDate,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((tickets) => {
          this.availablePrepaidTickets = [];
          tickets.forEach((ticket) => {
            if (ticket.maxPark !== undefined) {
              this.availablePrepaidTickets.unshift(ticket);
            }
          });
          if (
            this.bookingForm &&
            !(this.editBooking && this.editBooking.bookingId)
          ) {
            const usedByThisReg = tickets.find((t) =>
              t.bookings?.some(
                (b) =>
                  b.registrationNumber?.toUpperCase() === term?.toUpperCase()
              )
            );
            const usedByThisRegUsable =
              usedByThisReg && !this.ticketIsFull(usedByThisReg)
                ? usedByThisReg
                : undefined;
            const firstAvailable = this.availablePrepaidTickets.find(
              (t) => !this.ticketIsFull(t)
            );
            const preferred = usedByThisRegUsable || firstAvailable;
            const nextValue: any = preferred ? preferred : 'none';
            setTimeout(() => {
              const ctrl = this.bookingForm?.controls['prepaid'];
              ctrl?.setValue(nextValue, { emitEvent: true });
              ctrl?.markAsDirty();
              ctrl?.markAsTouched();
              ctrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
              this.cdr.detectChanges();
              this.updateOrder();
              if (preferred) {
                requestAnimationFrame(() => {
                  const c = this.bookingForm?.controls['prepaid'];
                  if (c && c.value === 'none') {
                    c.setValue(preferred, { emitEvent: true });
                    this.cdr.detectChanges();
                    this.updateOrder();
                  }
                });
              }
            }, 0);
          }
        });
    }
  }

  checkContactInformation(registrationNumber: string) {
    this.contactInformationApi
      .findContactInformation({ registrationNumber })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((contactInformation) => {
        if (contactInformation.length > 0) {
          const message = [
            `För- & efternamn: 
            ${contactInformation[0].name}`,
            `E-post:
            ${contactInformation[0].email}`,
            `Telefonummer:
            ${contactInformation[0].phone}`,
          ];

          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
              title: `Historisk kontaktinformation kopplat till registreringsnummer "${registrationNumber.toUpperCase()}"`,
              message: message,
              confirm: true,
              yesBtnText: 'Applicera',
              noBtnText: 'Ignorera',
            },
          });

          dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
              if (result && this.bookingForm) {
                this.bookingForm.controls['name'].setValue(
                  contactInformation[0].name
                );
                this.bookingForm.controls['email'].setValue(
                  contactInformation[0].email
                );
                this.bookingForm.controls['phoneNumber'].setValue(
                  contactInformation[0].phone
                );
              }
            });
        }
      });
  }

  ticketIsFull(ticket: any): boolean {
    return this.booking
      ? this.bookingService.ticketIsFull(ticket, this.booking)
      : false;
  }

  filterSelectedFeatures(): FeatureWithPriceDTO[] {
    return this.bookingForm
      ? this.bookingService.filterSelectedFeatures(
          this.bookingForm,
          this.availableMainFeatures
        )
      : [];
  }

  resetFormAfterDuplicate(): void {
    /* Reset formControls that's non-common for duplicate booking */
    const resetControls = [
      'location',
      'registrationNumber',
      'passengers',
      'notOnlyCarryOnLuggage',
      'discount',
      'prepaid',
      'passengers',
      'childSafetySeat',
      'childBoosterSeat',
    ];

    resetControls.forEach((control) => {
      this.bookingForm?.controls[control]?.reset(
        control === 'notOnlyCarryOnLuggage' ? true : undefined
      );
    });

    /* Reset vehicle/engine type to defaults (first options) */
    const defaultVehicleType = this.vehicleTypes?.[0] ?? undefined;
    const defaultEngineType = this.engineTypes?.[0] ?? undefined;

    if (this.bookingForm) {
      this.bookingForm.controls['vehicleType']?.setValue(defaultVehicleType);
      this.bookingForm.controls['engineType']?.setValue(defaultEngineType);
    }

    /* Keep radio helpers in sync so UI reflects selection */
    this.checkedVehicleType = defaultVehicleType;
    this.checkedEngineType = defaultEngineType;

    /* For each feature set checkbox to unchecked  */
    this.availableMainFeatures.forEach((mainFeature) => {
      mainFeature.features?.forEach((feature) => {
        if (feature.featureId !== undefined && this.bookingForm) {
          this.bookingForm.controls[feature.featureId].reset(undefined);
        }
      });
    });
    this.selectedFeatures = [];

    /* Recalculate after resets */
    this.updateFeatures();
    this.updateOrder();

    /* Location is the first field that needs new input for the next
       booking - scroll its heading into view so the user doesn't have to
       hunt for where to continue. Target the heading itself rather than the
       whole (possibly long) radio-button list, so the scroll always lands
       at the very start of the section regardless of how many parking
       options there are. Wait two animation frames so this runs after the
       browser has actually laid out the reset form (setTimeout(0) alone can
       still run before layout has settled). The extra margin needed to
       clear the pending-order box (fixed to the top on narrow screens) is
       reserved via `scroll-margin-top` on this heading in the stylesheet. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        (
          document.querySelector('.location-field h3') ||
          document.querySelector('.location-field')
        )?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /**
   * Navigate a newly created (non-edit) order to the checkout page.
   * In driver mode, the payment method is always "Betala senare" - the order is
   * checked out as pay-later automatically so the driver lands directly on the
   * checkout page's "Order genomförd" confirmation instead of picking a method.
   */
  proceedToCheckout(): void {
    const driverMode = this.store.selectSnapshot(UsersState.driverMode);
    const orderId = this.store.selectSnapshot(OrdersState.order)?.orderId;

    if (driverMode && orderId) {
      this.store
        .dispatch(new CheckoutOrderPayLater(orderId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.router.navigate(['hantera-betalning']);
          },
          error: (error) => {
            this.showNextSpinner = false;
            this.showDuplicateSpinner = false;
            console.error(
              'Error checking out order as pay later in driver mode: ',
              error
            );
            this.dialog.open(ErrorDialogComponent, {
              data: {
                title: 'Det gick inte att slutföra bokningen',
                message: [normalizeErrorMessage(error)],
              },
            });
          },
        });
    } else {
      this.router.navigate(['hantera-betalning']);
    }
  }

  onSubmit(duplicate: boolean) {
    if (!this.bookingForm?.controls['location'].value) {
      /* Angular Material does not handle styling for invalid radio buttons. This is a manual solution. */
      this.bookingForm?.get('location')?.markAsDirty();
    }
    if (this.bookingForm && this.bookingForm.invalid) {
      this.invalidFormMessage = 'Ett eller flera fält är ogiltiga';
    } else {
      if (duplicate) {
        this.showDuplicateSpinner = true;
      }
      if (this.booking) {
        /* If editing */
        if (
          this.editBooking &&
          this.editBooking.bookingId &&
          this.editOrder &&
          this.editOrder?.orderId
        ) {
          this.showNextSpinner = true;
          const bookingOrderItem = this.editOrder.orderItems?.find(
            (item) =>
              item.orderItemType === 'RESOURCE' &&
              item.booking?.bookingId === this.booking?.bookingId
          );
          if (bookingOrderItem) bookingOrderItem.booking = this.booking;

          if (bookingOrderItem?.orderItemId) {
            /* Update existing order */
            this.store
              .dispatch(
                new UpdateOrderItem(
                  this.editOrder.orderId,
                  bookingOrderItem.orderItemId,
                  bookingOrderItem
                )
              )
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: () => {
                  this.hasChanges = false;

                  // Check if any features are included in the booking
                  let featuresToHandle = false;
                  if (this.selectedFeatures.length > 0) {
                    featuresToHandle = true;
                  } else {
                    this.editOrder?.orderItems?.forEach((item) => {
                      if (item.feature) {
                        featuresToHandle = true;
                      }
                    });
                  }

                  // If features included, update them before navigating. If not, go to navigation
                  if (featuresToHandle && this.booking) {
                    if (this.selectedFeatures.length > 1)
                      this.showNextSpinner = true;
                    this.orderService
                      .updateOrderFeatures(
                        this.booking,
                        this.selectedFeatures,
                        this.editOrder
                      )
                      .pipe(takeUntil(this.onDestroy$))
                      .subscribe({
                        next: (v) => {
                          /* When finished updating, go to checkout */
                          this.hasChanges = false;
                          this.showDuplicateSpinner = false;
                          this.store
                            .selectOnce(OrdersState.order)
                            .pipe(takeUntil(this.onDestroy$))
                            .subscribe((order) => {
                              // Only navigate to checkout if price is different after editing
                              // (proceedToCheckout checks out as pay-later in driver mode)
                              if (
                                order.totalAmount !==
                                this.bookingPriceBeforeEdit
                              ) {
                                this.proceedToCheckout();
                              } else {
                                // Changes saved without payment, remove order from orderstate and go back to details
                                this.store
                                  .dispatch(new ResetOrderState())
                                  .pipe(takeUntil(this.onDestroy$))
                                  .subscribe(() => {
                                    this.editBooking?.bookingId &&
                                      this.router.navigate([
                                        'bokning',
                                        this.editBooking.bookingId,
                                      ]);
                                  });
                              }
                            });
                        },
                        error: (error) => {
                          console.error('Error updating features: ', error);
                          this.dialog
                            .open(ErrorDialogComponent, {
                              data: {
                                title: 'Det gick inte att spara bokningen',
                                message: [normalizeErrorMessage(error)],
                              },
                            })
                            .afterClosed()
                            .pipe(takeUntil(this.onDestroy$))
                            .subscribe(() => {
                              this.store.dispatch(new ResetOrderState());
                            });
                        },
                      });
                  } else {
                    // Navigation without updating features
                    this.hasChanges = false;

                    this.store
                      .selectOnce(OrdersState.order)
                      .pipe(takeUntil(this.onDestroy$))
                      .subscribe((order) => {
                        if (order.totalAmount !== this.bookingPriceBeforeEdit) {
                          this.proceedToCheckout();
                        } else {
                          this.store
                            .dispatch(new ResetOrderState())
                            .pipe(takeUntil(this.onDestroy$))
                            .subscribe(() => {
                              this.editBooking?.bookingId &&
                                this.router.navigate([
                                  'bokning',
                                  this.editBooking.bookingId,
                                ]);
                            });
                        }
                      });
                  }
                  this.showNextSpinner = false;
                  this.showDuplicateSpinner = false;
                },
                error: (error) => {
                  this.showNextSpinner = false;
                  this.showDuplicateSpinner = false;
                  console.error('Error updating order item: ', error);
                  this.dialog.open(ErrorDialogComponent, {
                    data: {
                      title: 'Det gick inte att spara bokningen',
                      message: [normalizeErrorMessage(error)],
                    },
                  });
                },
              });
          }
        } else {
          if (!this.showDuplicateSpinner) this.showNextSpinner = true;
          /* Add to new order */
          this.store
            .dispatch(
              new AddOrderItem({
                orderItemType: OrderItemDTOOrderItemTypeEnum.Resource,
                booking: this.booking,
              })
            )
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () => {
                const focusedElement = document.activeElement as HTMLElement;

                // Make sure the second operation gets called after the first
                if (
                  this.selectedFeatures.length &&
                  this.selectedFeatures.length >= 1
                ) {
                  this.orderService
                    .addOrderFeatures(this.selectedFeatures)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(() => {
                      if (duplicate) {
                        this.resetFormAfterDuplicate();
                        this.selectedFeatures = [];
                        this.updateBooking();
                        this.checkAvailability();
                        this.hasChanges = false;
                        this.showNextSpinner = false;
                        this.showDuplicateSpinner = false;

                        /* Blur focused element before opening order dialog */
                        focusedElement?.blur();
                      } else {
                        this.hasChanges = false;
                        this.showNextSpinner = false;
                        this.showDuplicateSpinner = false;
                        this.proceedToCheckout();
                      }
                    });
                } else {
                  if (duplicate) {
                    this.resetFormAfterDuplicate();
                    this.selectedFeatures = [];
                    this.updateBooking();
                    this.checkAvailability();
                    this.hasChanges = false;

                    /* Blur focused element before opening order dialog */
                    focusedElement?.blur();
                  } else {
                    this.hasChanges = false;
                    this.proceedToCheckout();
                  }
                }
                this.showNextSpinner = false;
                this.showDuplicateSpinner = false;
              },
              error: (error) => {
                this.showNextSpinner = false;
                this.showDuplicateSpinner = false;
                console.error('Error creating booking: ', error);

                this.dialog.open(ErrorDialogComponent, {
                  data: {
                    title: 'Det gick inte att spara bokningen',
                    message: [normalizeErrorMessage(error)],
                  },
                });
              },
            });
        }
      }
    }
  }
}
